import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Field,
  Input,
  Avatar,
  IconButton,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI, organizationsAPI, usersAPI } from '../services/api';
import { Organization } from '../types';
import { toast } from '../utils/toast';
import { resolveLogoUrl } from '../utils/logoUrl';
import {
  getStoredOrgOrder,
  setStoredOrgOrder,
  reconcileOrgOrder,
  sortOrganizationsByUserOrder,
} from '../utils/organizationPreferences';

interface OrgDisplayRow extends Organization {
  role?: 'owner' | 'admin' | 'member';
}

const Settings: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgOrderList, setOrgOrderList] = useState<OrgDisplayRow[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const myAvatarUrl = resolveLogoUrl(user?.avatarUrl || null);

  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    const uid = user?.id;
    if (!uid) {
      setOrgOrderList([]);
      setOrgsLoading(false);
      return;
    }
    (async () => {
      setOrgsLoading(true);
      try {
        const response = await organizationsAPI.getAll();
        const data = (Array.isArray(response.data) ? response.data : []) as OrgDisplayRow[];
        const ids = data.map((o) => o._id);
        const order = reconcileOrgOrder(getStoredOrgOrder(uid), ids);
        setStoredOrgOrder(uid, order);
        const sorted = sortOrganizationsByUserOrder(data, order);
        if (!cancelled) setOrgOrderList(sorted);
      } catch {
        if (!cancelled) setOrgOrderList([]);
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const moveOrg = (index: number, delta: -1 | 1) => {
    const uid = user?.id;
    if (!uid) return;
    setOrgOrderList((list) => {
      const next = [...list];
      const j = index + delta;
      if (j < 0 || j >= next.length) return list;
      const tmp = next[index];
      next[index] = next[j];
      next[j] = tmp;
      setStoredOrgOrder(
        uid,
        next.map((o) => o._id),
      );
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ title: 'Error', description: 'Please fill in all fields.', status: 'error' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Error', description: 'New password must be at least 8 characters.', status: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'New password and confirmation do not match.', status: 'error' });
      return;
    }
    setLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast({ title: 'Success', description: 'Password changed successfully.', status: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Password change failed.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMyAvatar = async (file?: File) => {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const response = await usersAPI.uploadMyAvatar(file);
      const avatarUrl = response.data?.avatarUrl || null;
      updateUser({ avatarUrl });
      toast({ title: 'Success', description: 'Profile image updated.', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Avatar upload failed.',
        status: 'error',
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Desktop sidebar */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        position="fixed"
        top={0}
        left={0}
        h="100vh"
        w="260px"
        bg="white"
        borderRightWidth={1}
        borderColor="gray.200"
        boxShadow="sm"
        p={4}
        flexDirection="column"
        zIndex={15}
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          opacity={0.06}
          pointerEvents="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src="/NS_Projects.png"
            alt="NS Projects watermark"
            style={{ maxWidth: '260px' }}
          />
        </Box>

        <Box
          position="relative"
          zIndex={1}
          display="flex"
          flexDirection="column"
          h="100%"
        >
          <Heading
            size="lg"
            mb={2}
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            Northstar Projects
          </Heading>
          <HStack gap={2} mb={6}>
            {myAvatarUrl ? (
              <img
                src={myAvatarUrl}
                alt={user?.name || 'User avatar'}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '9999px',
                  objectFit: 'cover',
                  border: '1px solid #e5e7eb',
                }}
              />
            ) : (
              <Avatar.Root size="sm">
                <Avatar.Fallback bg="gray.200" color="black">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </Avatar.Fallback>
              </Avatar.Root>
            )}
            <Text fontWeight="medium" color="gray.700">
              {user?.name}
            </Text>
          </HStack>
          <VStack align="stretch" gap={3} flex="1">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              borderRadius="lg"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'md',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Dashboard
            </Button>
            {(isMasterAdmin || isAdmin) && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                justifyContent="flex-start"
                width="100%"
              >
                Admin Panel
              </Button>
            )}
            <Button
              variant="solid"
              colorScheme="purple"
              borderRadius="lg"
              justifyContent="flex-start"
              width="100%"
            >
              Settings
            </Button>
          </VStack>
          <VStack
            align="stretch"
            gap={2}
            pt={3}
            borderTopWidth={1}
            borderColor="gray.100"
          >
            <Button
              variant="outline"
              onClick={logout}
              borderRadius="lg"
              _hover={{
                bg: 'gray.100',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Logout
            </Button>
          </VStack>
        </Box>
      </Box>

      {/* Header */}
      <Box bg="white" borderBottomWidth={1} borderColor="gray.200" boxShadow="sm">
        <Container maxW="container.xl" py={4}>
          <HStack justifyContent="space-between">
            <HStack gap={3} alignItems="center">
              <IconButton
                aria-label="Back to dashboard"
                variant="ghost"
                borderRadius="full"
                onClick={() => navigate('/dashboard')}
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </IconButton>
              <Heading size="xl" color="gray.800">
                Settings
              </Heading>
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Container
        maxW={{ base: 'container.md', md: '100%' }}
        py={8}
        pl={{ base: 0, md: '280px' }}
        pr={{ base: 0, md: 8 }}
      >
        <VStack gap={8} align="stretch">
          <Box bg="white" borderRadius="xl" p={8} boxShadow="sm" borderWidth={1} borderColor="gray.200">
            <Heading size="lg" color="gray.800" mb={6}>
              Profile image
            </Heading>
            <HStack gap={4} alignItems="center" flexWrap="wrap">
              {myAvatarUrl ? (
                <img
                  src={myAvatarUrl}
                  alt={user?.name || 'User avatar'}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '9999px',
                    objectFit: 'cover',
                    border: '1px solid #e5e7eb',
                  }}
                />
              ) : (
                <Avatar.Root size="lg">
                  <Avatar.Fallback bg="gray.200" color="black">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </Avatar.Fallback>
                </Avatar.Root>
              )}
              <Button
                as="label"
                variant="outline"
                cursor="pointer"
                loading={avatarUploading}
              >
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.currentTarget.value = '';
                    handleUploadMyAvatar(file);
                  }}
                />
              </Button>
            </HStack>
          </Box>

          <Box bg="white" borderRadius="xl" p={8} boxShadow="sm" borderWidth={1} borderColor="gray.200">
            <Heading size="lg" color="gray.800" mb={2}>
              Companies (order in menu)
            </Heading>
            <Text fontSize="sm" color="gray.600" mb={6}>
              The dashboard company selector uses this order for your account. Last selected company is
              restored after refresh automatically.
            </Text>
            {orgsLoading ? (
              <Text color="gray.500" fontSize="sm">
                Loading companies…
              </Text>
            ) : orgOrderList.length === 0 ? (
              <Text color="gray.500" fontSize="sm">
                No companies available.
              </Text>
            ) : (
              <VStack align="stretch" gap={2}>
                {orgOrderList.map((org, index) => (
                  <HStack
                    key={org._id}
                    p={3}
                    borderWidth={1}
                    borderColor="gray.200"
                    borderRadius="lg"
                    bg="gray.50"
                    justifyContent="space-between"
                    gap={3}
                  >
                    <Text fontWeight="medium" color="gray.800" truncate flex={1}>
                      {org.name}
                    </Text>
                    <HStack gap={1} flexShrink={0}>
                      <IconButton
                        aria-label="Move up"
                        size="sm"
                        variant="outline"
                        disabled={index === 0}
                        onClick={() => moveOrg(index, -1)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </IconButton>
                      <IconButton
                        aria-label="Move down"
                        size="sm"
                        variant="outline"
                        disabled={index === orgOrderList.length - 1}
                        onClick={() => moveOrg(index, 1)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </IconButton>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>
          <Box bg="white" borderRadius="xl" p={8} boxShadow="sm" borderWidth={1} borderColor="gray.200">
            <Heading size="lg" color="gray.800" mb={6}>Change password</Heading>
            <form onSubmit={handleSubmit}>
              <VStack gap={5} align="stretch">
                <Field.Root>
                  <label htmlFor="settings-current-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">Current password</Text>
                  </label>
                  <Input
                    id="settings-current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    size="lg"
                    borderRadius="lg"
                    mt={1}
                  />
                </Field.Root>
                <Field.Root>
                  <label htmlFor="settings-new-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">New password (min. 8 characters)</Text>
                  </label>
                  <Input
                    id="settings-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    size="lg"
                    borderRadius="lg"
                    minLength={8}
                    mt={1}
                  />
                </Field.Root>
                <Field.Root>
                  <label htmlFor="settings-confirm-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">Confirm new password</Text>
                  </label>
                  <Input
                    id="settings-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    size="lg"
                    borderRadius="lg"
                    mt={1}
                  />
                </Field.Root>
                <Button
                  type="submit"
                  size="lg"
                  loading={loading}
                  bgGradient="to-r"
                  gradientFrom="#273f96"
                  gradientTo="#1369b3"
                  color="white"
                  borderRadius="lg"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                >
                  Save password
                </Button>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Settings;
